import { isValidElement, lazy, type ComponentType, type ReactNode } from 'react';
import { matchRoutes, type RouteObject } from 'react-router-dom';
import i18n from 'i18next';

type PageModule = { default: ComponentType };
type Preload = () => Promise<PageModule>;

/**
 * `React.lazy` that loads the page chunk and its i18n namespaces in parallel.
 *
 * Navigations run inside `startTransition`, so the old page stays on screen until the new one
 * stops suspending. A plain `lazy()` suspends twice in a row — first on the chunk, then on
 * `useTranslation` fetching the namespace once the page renders. Loading both up front turns
 * that into one wait, and `preload` lets the sidebar start it on hover, before the click.
 */
export function lazyPage(load: () => Promise<PageModule>, namespaces: string[] = []) {
  let pending: Promise<PageModule> | undefined;

  const preload: Preload = () => {
    pending ??= Promise.all([load(), i18n.loadNamespaces(namespaces)]).then(
      ([module]) => module,
      (error) => {
        // Don't cache a failed hover-preload (e.g. dev server restarting): let the click retry.
        pending = undefined;
        throw error;
      }
    );
    return pending;
  };

  return Object.assign(lazy(preload), { preload });
}

// ----------------------------------------------------------------------

const registeredRoutes: RouteObject[] = [];

/** The lazy page inside a route element, looking through wrappers such as `CourseAccessGuard`. */
const findPreload = (node: ReactNode): Preload | undefined => {
  if (!isValidElement(node)) return undefined;

  const { preload } = node.type as { preload?: Preload };
  if (typeof preload === 'function') return preload;

  return findPreload((node.props as { children?: ReactNode }).children);
};

export function registerRoutePreloads(routes: RouteObject[]) {
  registeredRoutes.push(...routes);
}

/** Starts loading the page for `to` (path, optionally with a query) without navigating. */
export function preloadRoute(to: string) {
  const pathname = to.split(/[?#]/)[0];
  const matches = matchRoutes(registeredRoutes, pathname) ?? [];

  matches.forEach(({ route }) => {
    findPreload(route.element)?.().catch(() => {
      // Ignored: the navigation itself retries and surfaces the error.
    });
  });
}
